/**
 * Main App Component
 * Sets up routing for the analytics dashboard
 */

import { Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider, PermissionProvider, PermissionRoute } from '@shared/auth';
import { StandardPermission } from '@shared/auth/permissions';
import apiConfig from './config/apiConfig';
import AnalyticsLayout from './components/layout/AnalyticsLayout';
import AuthPage from './pages/AuthPage';
import OIDCCallback from './pages/auth/OIDCCallback';
import AccessDeniedPage from './pages/AccessDeniedPage';
import DashboardPage from './pages/DashboardPage';
import ExecutiveDashboard from './pages/ExecutiveDashboard';
import AgentViewPage from './pages/AgentViewPage';
import AgentDetailPage from './pages/AgentDetailPage';
import ConversationsPage from './pages/ConversationsPage';
import SentimentAnalysisPage from './pages/SentimentAnalysisPage';
import CoachingPage from './pages/CoachingPage';
import ReportsPage from './pages/ReportsPage';
import ConnectorsPage from './pages/ConnectorsPage';
import ConnectorInteractionsPage from './pages/ConnectorInteractionsPage';
import ConnectorInteractionDetailPage from './pages/ConnectorInteractionDetailPage';
import WebhooksPage from './pages/WebhooksPage';
import LocationHeatmapPage from './pages/LocationHeatmapPage';
import SettingsPage from './pages/SettingsPage';

function App() {
  return (
    <AuthProvider apiConfig={apiConfig} defaultReturnUrl="/dashboard">
      <PermissionProvider>
        <Routes>
          {/* Public auth routes */}
          <Route path="/auth" element={<AuthPage />} />
          <Route path="/auth/callback" element={<OIDCCallback />} />
          <Route path="/access-denied" element={<AccessDeniedPage />} />

          {/* Root redirect */}
          <Route path="/" element={<Navigate to="/dashboard" replace />} />

          {/* Dashboard */}
          <Route
            path="/dashboard"
            element={
              <PermissionRoute permission={StandardPermission.Analytics.READ}>
                <AnalyticsLayout title="Dashboard" subtitle="Overview">
                  <DashboardPage />
                </AnalyticsLayout>
              </PermissionRoute>
            }
          />

          {/* Dashboards */}
          <Route
            path="/executive"
            element={
              <PermissionRoute permission={StandardPermission.Analytics.READ}>
                <AnalyticsLayout title="Executive Dashboard" subtitle="High-level KPIs">
                  <ExecutiveDashboard />
                </AnalyticsLayout>
              </PermissionRoute>
            }
          />
          <Route
            path="/agent"
            element={
              <PermissionRoute permission={StandardPermission.Analytics.READ}>
                <AnalyticsLayout title="Agent View" subtitle="Agent queue">
                  <AgentViewPage />
                </AnalyticsLayout>
              </PermissionRoute>
            }
          />
          <Route
            path="/agent/:agentId"
            element={
              <PermissionRoute permission={StandardPermission.Analytics.READ}>
                <AnalyticsLayout title="Agent Detail" subtitle="Agent performance">
                  <AgentDetailPage />
                </AnalyticsLayout>
              </PermissionRoute>
            }
          />

          {/* Intelligence */}
          <Route
            path="/conversations"
            element={
              <PermissionRoute permission={StandardPermission.Analytics.READ}>
                <AnalyticsLayout title="Conversations" subtitle="All interactions">
                  <ConversationsPage />
                </AnalyticsLayout>
              </PermissionRoute>
            }
          />
          <Route
            path="/sentiment"
            element={
              <PermissionRoute permission={StandardPermission.Analytics.READ}>
                <AnalyticsLayout title="Sentiment Analysis" subtitle="Signal trends">
                  <SentimentAnalysisPage />
                </AnalyticsLayout>
              </PermissionRoute>
            }
          />
          <Route
            path="/locations"
            element={
              <PermissionRoute permission={StandardPermission.Analytics.READ}>
                <AnalyticsLayout title="Location Intelligence" subtitle="Geographic heatmap">
                  <LocationHeatmapPage />
                </AnalyticsLayout>
              </PermissionRoute>
            }
          />
          {/* Quality */}
          <Route
            path="/coaching"
            element={
              <PermissionRoute permission={StandardPermission.Analytics.READ}>
                <AnalyticsLayout title="Coaching" subtitle="Agent scorecards">
                  <CoachingPage />
                </AnalyticsLayout>
              </PermissionRoute>
            }
          />
          <Route
            path="/reports"
            element={
              <PermissionRoute permission={StandardPermission.Analytics.READ}>
                <AnalyticsLayout title="Reports" subtitle="Report builder">
                  <ReportsPage />
                </AnalyticsLayout>
              </PermissionRoute>
            }
          />
          <Route
            path="/scheduled-reports"
            element={
              <PermissionRoute permission={StandardPermission.Analytics.READ}>
                <AnalyticsLayout title="Scheduled Reports" subtitle="Automated reporting">
                  <ReportsPage />
                </AnalyticsLayout>
              </PermissionRoute>
            }
          />

          {/* Configuration */}
          <Route
            path="/connectors"
            element={
              <PermissionRoute permission={StandardPermission.Analytics.READ}>
                <AnalyticsLayout title="Connectors" subtitle="Helpdesk integrations">
                  <ConnectorsPage />
                </AnalyticsLayout>
              </PermissionRoute>
            }
          />
          <Route
            path="/connectors/:connectorId/interactions"
            element={
              <PermissionRoute permission={StandardPermission.Analytics.READ}>
                <AnalyticsLayout title="Connector Interactions" subtitle="Synced data">
                  <ConnectorInteractionsPage />
                </AnalyticsLayout>
              </PermissionRoute>
            }
          />
          <Route
            path="/connectors/:connectorId/interactions/:interactionId"
            element={
              <PermissionRoute permission={StandardPermission.Analytics.READ}>
                <AnalyticsLayout title="Interaction Detail" subtitle="Full interaction view">
                  <ConnectorInteractionDetailPage />
                </AnalyticsLayout>
              </PermissionRoute>
            }
          />
          <Route
            path="/webhooks"
            element={
              <PermissionRoute permission={StandardPermission.Analytics.READ}>
                <AnalyticsLayout title="Webhooks" subtitle="Event configuration">
                  <WebhooksPage />
                </AnalyticsLayout>
              </PermissionRoute>
            }
          />
          <Route
            path="/settings"
            element={
              <PermissionRoute permission={StandardPermission.Analytics.READ}>
                <AnalyticsLayout title="Settings" subtitle="Analytics settings">
                  <SettingsPage />
                </AnalyticsLayout>
              </PermissionRoute>
            }
          />

          {/* Catch all */}
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>

        <Toaster
          position="top-right"
          containerStyle={{ top: '72px' }}
          containerProps={{ 'aria-live': 'polite', role: 'status' }}
          reverseOrder={false}
          gutter={8}
          toastOptions={{
            duration: 4000,
            style: {
              background: '#363636',
              color: '#fff',
              borderRadius: '10px',
              fontSize: '14px',
            },
            success: {
              duration: 5000,
              iconTheme: {
                primary: '#10b981',
                secondary: '#fff',
              },
            },
            error: {
              duration: 5000,
              iconTheme: {
                primary: '#ef4444',
                secondary: '#fff',
              },
            },
          }}
        />
      </PermissionProvider>
    </AuthProvider>
  );
}

export default App;
